import React from 'react';

interface OrderTrackingProps {
  status: string;
  orderNumber?: string;
}

const statusSteps = [
  { key: 'pending', label: 'Order Placed', icon: '📝' },
  { key: 'received', label: 'Received', icon: '✅' },
  { key: 'preparing', label: 'Preparing', icon: '☕' },
  { key: 'ready', label: 'Ready', icon: '🎉' },
  { key: 'completed', label: 'Completed', icon: '✨' }
];

export default function OrderTracking({ status, orderNumber }: OrderTrackingProps) {
  const getStatusIndex = (currentStatus: string): number => {
    const index = statusSteps.findIndex(step => step.key === currentStatus);
    return index >= 0 ? index : 0;
  };

  const currentIndex = getStatusIndex(status);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 dark:bg-gray-800 dark:text-gray-100">
      <h3 className="text-xl font-bold mb-6 text-amber-800 dark:text-amber-200">
        Order Tracking {orderNumber && `#${orderNumber}`}
      </h3>
      
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700">
          <div
            className="absolute top-0 left-0 w-full bg-amber-600 transition-all duration-500 ease-in-out"
            style={{
              height: `${(currentIndex / (statusSteps.length - 1)) * 100}%`
            }}
          />
        </div>

        {/* Status Steps */}
        <div className="space-y-8">
          {statusSteps.map((step, index) => {
            const isCompleted = index <= currentIndex;
            const isCurrent = index === currentIndex;
            const isUpcoming = index > currentIndex;

            return (
              <div key={step.key} className="relative flex items-start gap-4">
                {/* Icon Circle */}
                <div
                  className={`relative z-10 flex items-center justify-center w-16 h-16 rounded-full border-4 transition-all duration-500 ${
                    isCompleted
                      ? 'bg-amber-600 border-amber-600 shadow-lg scale-110'
                      : isCurrent
                      ? 'bg-amber-200 border-amber-600 animate-pulse'
                      : 'bg-gray-100 border-gray-300 dark:bg-gray-700 dark:border-gray-600'
                  }`}
                >
                  <span
                    className={`text-2xl transition-all duration-300 ${
                      isCompleted ? 'scale-125' : isCurrent ? 'scale-110' : 'opacity-50'
                    }`}
                  >
                    {step.icon}
                  </span>
                  {isCompleted && index < currentIndex && (
                    <span className="absolute -top-1 -right-1 text-white bg-green-500 rounded-full w-5 h-5 flex items-center justify-center text-xs">
                      ✓
                    </span>
                  )}
                </div>

                {/* Label */}
                <div className="flex-1 pt-3">
                  <div
                    className={`font-semibold text-lg transition-colors duration-300 ${
                      isCompleted
                        ? 'text-amber-800 dark:text-amber-300'
                        : isCurrent
                        ? 'text-amber-700 dark:text-amber-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {step.label}
                  </div>
                  {isCurrent && (
                    <div className="mt-1 text-sm text-amber-600 dark:text-amber-400 animate-pulse">
                      In progress...
                    </div>
                  )}
                  {isUpcoming && (
                    <div className="mt-1 text-sm text-gray-400 dark:text-gray-600">
                      Coming up
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status-specific messages */}
      {status === 'cancelled' && (
        <div className="mt-6 p-4 bg-red-100 border border-red-300 rounded-lg dark:bg-red-900 dark:border-red-700">
          <p className="text-red-800 dark:text-red-200 font-semibold">
            ❌ Order Cancelled
          </p>
          <p className="text-red-600 dark:text-red-300 text-sm mt-1">
            This order has been cancelled and will not be processed.
          </p>
        </div>
      )}
    </div>
  );
}
